


















































































import {
	computed,
	defineComponent,
	onMounted,
	PropType,
	Ref,
	ref,
} from '@vue/composition-api';
import {
	ContentType,
	DiscussionMessage,
	DiscussionContentItem,
	ReactionType,
} from '@front-app/services/GroupsApi/DiscussionModel';
import { useReactionButtons } from '@front-app/components/buttons/useReactionButtons';
import { timeAgo } from '@front-app/util/time';
import { useDiscussionFollow } from '@front-app/components/discussion/useDiscussionFollow';
import { PartialUser } from '@front-app/services/UserApi/UserModel';
import { getUsername } from '@front-app/util/user';
import { htmlToText } from '@front-app/util/content';
import { Size } from '@front-app/components/constants';
import AddReactionDropdown from '@front-app/components/dropdowns/AddReactionDropdown.vue';
import ReactionsListDropdown from '@front-app/components/dropdowns/ReactionsListDropdown.vue';
import UserLink from '@front-app/components/buttons/UserLink.vue';
import FollowButton from '@front-app/components/buttons/concrete/FollowButton.vue';
import BaseContentCard from './BaseContentCard.vue';
import ContentCardIcon from '../../icons/LargeRoundIcon.vue';

export default defineComponent({
	components: {
		BaseContentCard,
		ContentCardIcon,
		FollowButton,
		ReactionsListDropdown,
		AddReactionDropdown,
		UserLink,
	},
	props: {
		content: {
			type: Object as PropType<DiscussionContentItem>,
			required: true,
		},
		compact: Boolean,
	},
	setup(props, ctx) {
		const getUsernameInternal = (user: PartialUser): string => getUsername(ctx.root.$t.bind(ctx.root), user);
		const shortenedDescription = computed(() => htmlToText(props.content.description, 400));
		const contentTime = computed(() => timeAgo(ctx.root.$tc.bind(ctx.root), new Date(props.content.createdDateTime)));
		const {
			isFollowing,
			isSubmittingFollow,
			submitToggleFollow,
		} = useDiscussionFollow(computed(() => props.content));
		const toggleFollowAndEmit = async () => {
			await submitToggleFollow();
			ctx.emit('toggle-follow');
		};
		const isUnread = computed(() => !props.content.metadata.discussion?.read);
		const messagesAndAttachments = computed(() => props.content
			.metadata
			.discussion
			?.messages
			.flatMap((message) => [
				message,
				...(message?.attachments?.contents || []),
			]) || []);
		const numPosts = computed(() => messagesAndAttachments.value.length);
		const numNewPosts = computed(() => {
			const unreadPosts = props.content.metadata.discussion?.messages.filter((message) => !message.read);
			return unreadPosts.length;
		});

		// Reactions
		const discussionMessages = computed(() => props.content.metadata.discussion.messages);
		const firstMessageReactions = computed(() => discussionMessages.value[0].reactions);
		const { reactionButtons } = useReactionButtons(firstMessageReactions);
		const activeReactionButtons = computed(() => reactionButtons.value.filter((reactionButton) => reactionButton?.count > 0));
		const reactToFirstMessage = (reactionType: ReactionType) => {
			const firstMessageId = discussionMessages.value[0].id;
			ctx.emit('react-to-content', ContentType.DISCUSSION, firstMessageId, reactionType, props.content.id);
		};

		// Footer
		const hasAnyReply = computed(() => props.content.metadata.discussion.messages.length > 1);
		const lastPost: Ref<DiscussionMessage> = computed(() => {
			const { messages } = props.content.metadata.discussion;
			return messages[messages.length - 1];
		});
		const lastPostAuthor = computed(() => lastPost.value.sender);
		const lastPostTimeAgo = computed(() => timeAgo(ctx.root.$tc.bind(ctx.root), new Date(lastPost.value.created)));

		// Responsive view
		const screenWidth = ref(window.innerWidth);
		onMounted(() => {
			window.onresize = () => {
				screenWidth.value = window.innerWidth;
			};
		});
		// eslint-disable-next-line arrow-body-style
		const getIconSize = () => {
			return screenWidth.value < 810 ? Size.MEDIUM : Size.LARGE;
		};

		return {
			numPosts,
			numNewPosts,
			isUnread,
			getIconSize,
			getUsername: getUsernameInternal,
			// Constants
			ContentType,
			// Discussion content
			shortenedDescription,
			contentTime,
			// Following...
			isFollowing,
			isSubmittingFollow,
			toggleFollowAndEmit,
			// Reactions
			reactionButtons,
			activeReactionButtons,
			reactToFirstMessage,
			// Footer
			hasAnyReply,
			lastPostAuthor,
			lastPostTimeAgo,
		};
	},
});
